import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/Select';
import ExternalLinksEditor from './ExternalLinksEditor';
import ImageGallery from './ImageGallery';
import DocumentsList from './DocumentsList';
import { loadContractors } from '../../utils/storage';
import { Building2, Search, X, Plus } from 'lucide-react';

const AssetForm = ({ asset, onSave, onCancel, assetTypes, onAutoSave }) => {
  const [formData, setFormData] = useState({});
  const [selectedType, setSelectedType] = useState('');
  const [savedAsset, setSavedAsset] = useState(asset);
  const [images, setImages] = useState(asset?.images || []);
  const [documents, setDocuments] = useState(asset?.documents || []);
  const [contractors, setContractors] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState(asset?.contractors || []);
  const [contractorSearch, setContractorSearch] = useState('');
  const [showContractorDropdown, setShowContractorDropdown] = useState(false);
  const isInitialMount = useRef(true);
  const contractorDropdownRef = useRef(null);

  useEffect(() => {
    const fetchContractors = async () => {
      const data = await loadContractors();
      setContractors(data);
    };
    fetchContractors();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contractorDropdownRef.current && !contractorDropdownRef.current.contains(event.target)) {
        setShowContractorDropdown(false);
      }
    };

    if (showContractorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showContractorDropdown]);

  useEffect(() => {
    if (asset) {
      const initialData = {
        name: asset.name || '',
        type: asset.type || '',
        externalLinks: asset.externalLinks || [],
        ...asset
      };
      setFormData(initialData);
      setSelectedType(asset.type || '');
      setSavedAsset(asset);
      setImages(asset.images || []);
      setDocuments(asset.documents || []);
      setSelectedContractors(asset.contractors || []);
    } else if (assetTypes.length > 0) {
      const defaultType = assetTypes[0]?.value || '';
      setFormData({
        name: '',
        type: defaultType,
        externalLinks: [],
      });
      setSelectedType(defaultType);
      setSavedAsset(null);
      setImages([]);
      setDocuments([]);
      setSelectedContractors([]);
    }
    isInitialMount.current = false;
  }, [asset, assetTypes]);

  const currentType = assetTypes.find(t => t.value === selectedType);
  const currentAttributes = currentType?.attributes || [];

  useEffect(() => {
    // When type changes (user-initiated), initialize form data with empty values for new type's attributes
    if (!isInitialMount.current && selectedType && assetTypes.length > 0) {
      const typeObj = assetTypes.find(t => t.value === selectedType);
      const attributes = typeObj?.attributes || [];
      
      setFormData(prev => {
        const newFormData = { ...prev, type: selectedType };
        
        // Initialize all attributes for the current type
        attributes.forEach(attr => {
          if (newFormData[attr.name] === undefined) {
            newFormData[attr.name] = '';
          }
        });
        
        // Remove attributes that don't belong to the new type
        Object.keys(newFormData).forEach(key => {
          if (key !== 'type' && key !== 'id' && key !== 'maintenanceRecords') {
            const hasAttribute = attributes.some(attr => attr.name === key);
            if (!hasAttribute) {
              delete newFormData[key];
            }
          }
        });
        
        return newFormData;
      });
    }
  }, [selectedType, assetTypes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
    setFormData(prev => ({ ...prev, type: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredAttrs = currentAttributes.filter(attr => attr.required);
    for (const attr of requiredAttrs) {
      if (!formData[attr.name] || formData[attr.name].toString().trim() === '') {
        alert(`${attr.label} is required`);
        return;
      }
    }

    // Include images, documents, and contractors in the save data
    const saveData = {
      ...formData,
      images,
      documents,
      contractors: selectedContractors
    };
    onSave(saveData);
  };

  // Auto-save function for new assets (called before first upload)
  const ensureAssetExists = async () => {
    if (!savedAsset && onAutoSave) {
      // Validate minimum required fields
      const nameAttr = currentAttributes.find(attr => attr.name === 'name');
      if (!formData.name || formData.name.trim() === '') {
        alert('Please enter a name for the asset before uploading files.');
        return null;
      }

      // Auto-save the asset
      const newAsset = await onAutoSave({
        ...formData,
        images: [],
        documents: [],
        maintenanceRecords: [],
        contractors: selectedContractors
      });
      
      if (newAsset) {
        setSavedAsset(newAsset);
        return newAsset;
      }
    }
    return savedAsset;
  };

  const handleImageUpdate = (updatedImages) => {
    setImages(updatedImages);
    // Update saved asset if it exists
    if (savedAsset) {
      setSavedAsset({ ...savedAsset, images: updatedImages });
    }
  };

  const handleDocumentUpdate = (updatedDocuments) => {
    setDocuments(updatedDocuments);
    // Update saved asset if it exists
    if (savedAsset) {
      setSavedAsset({ ...savedAsset, documents: updatedDocuments });
    }
  };

  const renderField = (attr) => {
    const value = formData[attr.name] || '';
    
    switch (attr.type) {
      case 'textarea':
        return (
          <textarea
            name={attr.name}
            value={value}
            onChange={handleChange}
            className="flex w-full rounded-lg border border-input bg-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[100px]"
            placeholder={`Enter ${attr.label.toLowerCase()}...`}
            required={attr.required}
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            name={attr.name}
            value={value}
            onChange={handleChange}
            placeholder={`0${attr.name === 'cost' ? '.00' : ''}`}
            step={attr.name === 'cost' ? '0.01' : '1'}
            min="0"
            required={attr.required}
          />
        );
      case 'date':
        return (
          <Input
            type="date"
            name={attr.name}
            value={value}
            onChange={handleChange}
            required={attr.required}
          />
        );
      case 'text':
      default:
        return (
          <Input
            name={attr.name}
            value={value}
            onChange={handleChange}
            placeholder={`e.g., ${getPlaceholderForField(attr.name)}`}
            required={attr.required}
            list={attr.name === 'location' ? 'location-list' : undefined}
          />
        );
    }
  };

  const getPlaceholderForField = (fieldName) => {
    const placeholders = {
      name: 'Hot Water Heater',
      serialNumber: 'ABC123456',
      model: 'XG50T12HE40U1',
      vin: '1HGBH41JXMN109186',
      make: 'Toyota',
      year: '2020',
      location: 'Kitchen, Master Bath, Garage...'
    };
    return placeholders[fieldName] || 'Enter value';
  };

  const handleExternalLinksChange = (links) => {
    setFormData(prev => ({ ...prev, externalLinks: links }));
  };

  const locationSuggestions = [
    'Kitchen', 'Master Bath', 'Guest Bath', 'Garage', 'Basement',
    'Living Room', 'Bedroom', 'Office', 'Attic', 'Utility Room', 'Laundry Room'
  ];

  // Group attributes for better layout
  const textFields = currentAttributes.filter(attr => 
    attr.type === 'text' && attr.name !== 'name'
  );
  const dateFields = currentAttributes.filter(attr => attr.type === 'date');
  const numberFields = currentAttributes.filter(attr => attr.type === 'number');
  const nameAttr = currentAttributes.find(attr => attr.name === 'name');
  const textareaAttr = currentAttributes.find(attr => attr.type === 'textarea');

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name field - always first */}
        {nameAttr && (
          <div>
            <label className="block text-sm font-medium mb-2">
              {nameAttr.label} <span className="text-primary">*</span>
            </label>
            {renderField(nameAttr)}
          </div>
        )}

        {/* Type selector */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Type <span className="text-primary">*</span>
          </label>
          <Select 
            value={selectedType} 
            onValueChange={handleTypeChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {assetTypes.filter(type => type.value && type.value.trim() !== '').map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date fields */}
        {dateFields.map(attr => (
          <div key={attr.name}>
            <label className="block text-sm font-medium mb-2">
              {attr.label}
              {attr.required && <span className="text-primary"> *</span>}
            </label>
            {renderField(attr)}
          </div>
        ))}

        {/* Text fields */}
        {textFields.map(attr => (
          <div key={attr.name}>
            <label className="block text-sm font-medium mb-2">
              {attr.label}
              {attr.required && <span className="text-primary"> *</span>}
            </label>
            {renderField(attr)}
          </div>
        ))}

        {/* Number fields */}
        {numberFields.map(attr => (
          <div key={attr.name}>
            <label className="block text-sm font-medium mb-2">
              {attr.label}
              {attr.required && <span className="text-primary"> *</span>}
            </label>
            {renderField(attr)}
          </div>
        ))}
      </div>

      {/* Textarea field - full width */}
      {textareaAttr && (
        <div>
          <label className="block text-sm font-medium mb-2">
            {textareaAttr.label}
            {textareaAttr.required && <span className="text-primary"> *</span>}
          </label>
          {renderField(textareaAttr)}
        </div>
      )}

      {/* External Links Editor */}
      <div>
        <ExternalLinksEditor
          links={formData.externalLinks || []}
          onChange={handleExternalLinksChange}
        />
      </div>

      {/* Contractors Selection */}
      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
        <label className="block text-sm font-medium mb-3 flex items-center space-x-2">
          <Building2 className="h-4 w-4" />
          <span>Attached Contractors</span>
        </label>
        
        {/* Selected Contractors as Tags */}
        {selectedContractors.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {selectedContractors.map((contractorId) => {
              const contractor = contractors.find(c => c.id === contractorId);
              if (!contractor) return null;
              return (
                <div
                  key={contractorId}
                  className="inline-flex items-center space-x-2 bg-primary/20 text-foreground rounded-md px-3 py-1.5 text-sm border border-primary/30"
                >
                  <span>{contractor.companyName || 'Unnamed Company'}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedContractors(selectedContractors.filter(id => id !== contractorId));
                    }}
                    className="hover:bg-primary/30 rounded p-0.5 transition-colors"
                    title="Remove contractor"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Search and Add Contractor */}
        {contractors.length === 0 ? (
          <p className="text-sm text-muted-foreground mb-2">
            No contractors available. <Link to="/contractors" className="text-primary hover:underline">Add contractors</Link> first.
          </p>
        ) : (
          <div className="relative" ref={contractorDropdownRef}>
            <div className="relative">
              <Input
                type="text"
                placeholder="Search contractors to add..."
                value={contractorSearch}
                onChange={(e) => {
                  setContractorSearch(e.target.value);
                  setShowContractorDropdown(true);
                }}
                onFocus={() => setShowContractorDropdown(true)}
                icon={<Search className="h-4 w-4" />}
                className="w-full"
              />
              {showContractorDropdown && contractorSearch && (() => {
                const filtered = contractors.filter(contractor => {
                  const searchLower = contractorSearch.toLowerCase();
                  const companyName = (contractor.companyName || '').toLowerCase();
                  const contactName = (contractor.contactName || '').toLowerCase();
                  const phone = (contractor.phone || '').toLowerCase();
                  const email = (contractor.email || '').toLowerCase();
                  return (
                    (companyName.includes(searchLower) ||
                     contactName.includes(searchLower) ||
                     phone.includes(searchLower) ||
                     email.includes(searchLower)) &&
                    !selectedContractors.includes(contractor.id)
                  );
                });

                return filtered.length > 0 ? (
                  <div className="absolute z-10 w-full mt-1 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg rounded-lg border border-white/20 shadow-lg max-h-60 overflow-y-auto">
                    {filtered.map((contractor) => (
                      <button
                        key={contractor.id}
                        type="button"
                        onClick={() => {
                          if (!selectedContractors.includes(contractor.id)) {
                            setSelectedContractors([...selectedContractors, contractor.id]);
                            setContractorSearch('');
                            setShowContractorDropdown(false);
                          }
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-primary/10 transition-colors flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-foreground">
                            {contractor.companyName || 'Unnamed Company'}
                          </div>
                          {contractor.contactName && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {contractor.contactName}
                            </div>
                          )}
                          {contractor.phone && (
                            <div className="text-xs text-muted-foreground">
                              {contractor.phone}
                            </div>
                          )}
                        </div>
                        <Plus className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="absolute z-10 w-full mt-1 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg rounded-lg border border-white/20 shadow-lg">
                    <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                      No matching contractors found
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Image Gallery */}
      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
        <ImageGallery
          assetId={savedAsset?.id}
          images={images}
          onUpdate={handleImageUpdate}
          onBeforeUpload={ensureAssetExists}
        />
      </div>

      {/* Documents List */}
      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
        <DocumentsList
          assetId={savedAsset?.id}
          documents={documents}
          onUpdate={handleDocumentUpdate}
          onBeforeUpload={ensureAssetExists}
        />
      </div>

      {/* Location suggestions datalist */}
      {currentAttributes.some(attr => attr.name === 'location') && (
        <datalist id="location-list">
          {locationSuggestions.map(loc => (
            <option key={loc} value={loc} />
          ))}
        </datalist>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 space-y-2 space-y-reverse sm:space-y-0">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {asset ? 'Update' : 'Add'} Asset
        </Button>
      </div>
    </form>
  );
};

export default AssetForm;